import React, { useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import CollapsibleBoxComponent from '../../../components/CollapsibleBox/CollapsibleBoxComponent';
import TextFieldComponent from '../../../components/TextField/TextFieldComponent';
import DropdownComponent from '../../../components/DropDown/DropDownComponent';
import driverTemplate from '../../../json/components/subscreens/DriverTemplate.json';
import {
  profileAddVehicleDriver,
  profileDeleteVehicleDriver,
} from '../../../api/apiServices';
import LoadingDots from '../../../components/LoadingDots/LoadingDots';
import { withMinDuration } from '../../../utils/withMinDuration';
import { normalizeCellNumber } from '../../../utils/questionEngineOtpHelpers';
import useValidationRegistry from '../useValidationRegistry';

const componentMap = {
  'text-field': TextFieldComponent,
  dropdown: DropdownComponent,
};

const getFreshDriver = () => JSON.parse(JSON.stringify(driverTemplate));

const formatDateForDriverField = (rawDate) => {
  if (!rawDate) return '';
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const normalizeLicenseType = (licenseType) => {
  const value = String(licenseType || '')
    .trim()
    .toLowerCase();

  if (value === 'rsa' || value === "sa driver's license" || value === 'full') {
    return "SA Driver's License";
  }

  if (value === 'learner' || value === "sa learner's license") {
    return "SA Learner's License";
  }

  if (value === 'international' || value === 'international license') {
    return 'International License';
  }

  return '';
};

const mapLicenseTypeToApiValue = (licenseType) => {
  const normalized = normalizeLicenseType(licenseType);

  if (normalized === "SA Driver's License") return 'RSA';
  if (normalized === "SA Learner's License") return 'Learner';
  if (normalized === 'International License') return 'International';

  return licenseType || '';
};

const normalizeMaritalStatus = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'cohabitating' || normalized === 'partneredcohabiting') {
    return 'Cohabitating';
  }

  if (
    normalized === 'separated' ||
    normalized === 'apartseperated' ||
    normalized === 'apartseparated'
  ) {
    return 'Separated';
  }

  if (normalized === 'single') return 'Single';
  if (normalized === 'married') return 'Married';
  if (normalized === 'divorced') return 'Divorced';
  if (normalized === 'widowed') return 'Widowed';

  return value || '';
};

const normalizeInsuranceDuration = (rawValue) => {
  const value = String(rawValue ?? '').trim();
  const lowerValue = value.toLowerCase();

  if (lowerValue === 'a' || lowerValue === 'never') return 'Never';
  if (value === '<1') return '<1';

  const numeric = Number(value);

  if (!Number.isNaN(numeric)) {
    if (numeric >= 7) return '7+';
    if (numeric <= 0) return '<1';
    return String(numeric);
  }

  return ['Never', '<1', '1', '2', '3', '4', '5', '6', '7+'].includes(value)
    ? value
    : '';
};

const normalizeLastClaim = (rawValue) => {
  const value = String(rawValue ?? '').trim();
  const lowerValue = value.toLowerCase();

  if (lowerValue === 'a' || lowerValue === 'never') return 'Never';
  if (value === '<1') return '<1';

  const numeric = Number(value);
  if (!Number.isNaN(numeric)) {
    if (numeric >= 7) return '7+';
    if (numeric <= 0) return '<1';
    return String(numeric);
  }

  return ['Never', '<1', '1', '2', '3', '4', '5', '6', '7+'].includes(value)
    ? value
    : '';
};

const getDriverPrimaryKey = (driver = {}) =>
  driver?.VehicleDriverPK ??
  driver?.VehicleDriverPk ??
  driver?.DriverPk ??
  driver?.vehicleDriverPK ??
  driver?.vehicleDriverPk ??
  driver?.DriverPK ??
  driver?.driverPK ??
  driver?.driverPk ??
  null;

const splitFullName = (fullName = '') => {
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return { driverName: '', driverLastName: '' };

  const [driverName, ...rest] = trimmed.split(/\s+/);
  return {
    driverName: driverName || '',
    driverLastName: rest.join(' '),
  };
};

const formatDateForDriverApi = (rawDate) => {
  const value = String(rawDate || '').trim();
  if (!value) return '';

  const normalized = value.replace(/\//g, '-');
  const isoLike = /^\d{4}-\d{2}-\d{2}$/.test(normalized)
    ? `${normalized}T00:00:00`
    : value;

  const parsed = new Date(isoLike);
  if (Number.isNaN(parsed.getTime())) return '';

  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}T00:00:00`;
};

const buildAddDriverPayload = (driver, policyHolderIdentifierRaw) => {
  const { driverName, driverLastName } = splitFullName(driver?.fullName?.value);

  // normalizeCellNumber rewrites a leading "27" to "0", which would corrupt
  // an email-based identifier (e.g. "27smith@gmail.com") — only normalize
  // when the resolved identifier is actually a cell number.
  const policyHolderIdentifier = String(
    policyHolderIdentifierRaw || ''
  ).includes('@')
    ? String(policyHolderIdentifierRaw).trim()
    : normalizeCellNumber(policyHolderIdentifierRaw);

  return {
    driverName,
    driverLastName,
    dateOfBirth: formatDateForDriverApi(driver?.dob?.value),
    policyHolderCellphoneNumber: policyHolderIdentifier,
    maritalStatus: normalizeMaritalStatus(driver?.maritalStatus?.value) || '',
    gender: driver?.gender?.value || '',
    dateLicenseObtained: formatDateForDriverApi(driver?.licenseYear?.value),
    licenseType: mapLicenseTypeToApiValue(driver?.licenseType?.value),
    comprehensiveInsDuration: driver?.uninterruptedInsurance?.value || '',
    lastClaimDuration: driver?.lastClaim?.value || '',
  };
};

const mapDriverToTemplate = (driver = {}) => {
  const nextDriver = getFreshDriver();
  nextDriver.VehicleDriverPK = getDriverPrimaryKey(driver);

  const firstName =
    driver?.DriverName || driver?.Name || driver?.firstName || '';
  const lastName =
    driver?.DriverLastName || driver?.Surname || driver?.lastName || '';

  nextDriver.fullName.value = [firstName, lastName].filter(Boolean).join(' ');
  nextDriver.dob.value = formatDateForDriverField(
    driver?.DateOfBirth || driver?.dateOfBirth
  );
  nextDriver.maritalStatus.value = normalizeMaritalStatus(
    driver?.MaritalStatus || driver?.maritalStatus
  );
  nextDriver.gender.value = driver?.Gender || driver?.gender || '';
  nextDriver.licenseType.value = normalizeLicenseType(
    driver?.LicenseType || driver?.LicenseTypeName || driver?.licenseType
  );
  nextDriver.licenseYear.value = formatDateForDriverField(
    driver?.DateLicenseObtained || driver?.dateLicenseObtained
  );
  nextDriver.uninterruptedInsurance.value = normalizeInsuranceDuration(
    driver?.ComprehensiveInsDuration || driver?.comprehensiveInsDuration
  );
  nextDriver.lastClaim.value = normalizeLastClaim(
    driver?.LastClaimDuration || driver?.lastClaimDuration
  );

  return nextDriver;
};

// Pre-live: the per-driver Reset action is temporarily disabled. Flip to true
// to restore it post-live.
const ALLOW_DRIVER_RESET = false;

const DriverDetailsContent = ({
  isMobile,
  savedDriversData = [],
  onResetDrivers,
  policyHolderIdentifier = '',
}) => {
  const { registerValidation, validateAll } = useValidationRegistry();
  const [savedDrivers, setSavedDrivers] = useState([]);
  const [showNewDriver, setShowNewDriver] = useState(false);
  const [newDriver, setNewDriver] = useState(getFreshDriver());
  const [isSavingNewDriver, setIsSavingNewDriver] = useState(false);
  const [deletingDriverIndices, setDeletingDriverIndices] = useState(
    () => new Set()
  );
  const [resettingDriverIndices, setResettingDriverIndices] = useState(
    () => new Set()
  );

  useEffect(() => {
    setSavedDrivers(
      Array.isArray(savedDriversData)
        ? savedDriversData.map((driver) => mapDriverToTemplate(driver))
        : []
    );
  }, [savedDriversData]);

  const handleNewChange = (fieldName, value) => {
    const updated = { ...newDriver };
    updated[fieldName].value = value;
    setNewDriver(updated);
  };

  const handleSavedChange = (index, fieldName, value) => {
    const updated = [...savedDrivers];
    updated[index][fieldName].value = value;
    setSavedDrivers(updated);
  };

  const addNewDriverToSaved = async () => {
    if (!validateAll()) {
      return;
    }

    setIsSavingNewDriver(true);
    try {
      await withMinDuration(
        (async () => {
          await profileAddVehicleDriver(
            buildAddDriverPayload(newDriver, policyHolderIdentifier)
          );
          await onResetDrivers?.(undefined, {
            showLoader: false,
            forceRefresh: true,
          });
        })()
      );
      setNewDriver(getFreshDriver());
      setShowNewDriver(false);
    } catch (error) {
      // ignore
    } finally {
      setIsSavingNewDriver(false);
    }
  };

  const deleteSavedDriver = async (index) => {
    const driver = savedDrivers[index];
    const vehicleDriverPK = getDriverPrimaryKey(driver);

    setDeletingDriverIndices((prev) => new Set(prev).add(index));
    try {
      await withMinDuration(
        (async () => {
          if (vehicleDriverPK) {
            await profileDeleteVehicleDriver(vehicleDriverPK);
          }
          await onResetDrivers?.(undefined, {
            showLoader: false,
            forceRefresh: true,
          });
        })()
      );
    } catch (error) {
      // ignore
    } finally {
      setDeletingDriverIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const resetSavedDriver = async (index) => {
    setResettingDriverIndices((prev) => new Set(prev).add(index));
    try {
      await withMinDuration(
        onResetDrivers?.(undefined, {
          showLoader: false,
          forceRefresh: true,
        })
      );
    } catch (error) {
      // ignore
    } finally {
      setResettingDriverIndices((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const renderDriverFields = (
    driver,
    onChange,
    index = null,
    registerFieldValidation = null,
    isReadOnly = false
  ) => {
    const entries = Object.entries(driver);

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 416px)',
          columnGap: isMobile ? 0 : '32px',
          rowGap: isMobile ? 4 : '16px',
          justifyContent: 'center',
          alignItems: 'start',
        }}
      >
        {entries.map(([fieldKey, config]) => {
          if (!config || typeof config !== 'object' || !config.type) {
            return null;
          }

          const Component = componentMap[config.type];
          if (!Component) return null;
          const isFullWidth = config.customSize === 'accounts-full';

          return (
            <Box
              key={fieldKey}
              sx={{
                width: '100%',
                gridColumn: !isMobile && isFullWidth ? '1 / -1' : 'auto',
              }}
            >
              <Component
                name={fieldKey}
                question={config}
                value={config.value}
                onChange={(name, value) =>
                  index === null
                    ? onChange(name, value)
                    : onChange(index, name, value)
                }
                registerValidation={registerFieldValidation}
                isMobile={isMobile}
                layoutVariant="account"
                readOnly={isReadOnly}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* Saved Drivers */}
      {savedDrivers.map((driver, index) => (
        <CollapsibleBoxComponent
          key={index}
          title={
            driver.fullName?.value || driver.FullName || `Driver ${index + 1}`
          }
          isMobile={isMobile}
          defaultExpanded={false}
        >
          <Box sx={{ px: isMobile ? 1.5 : 2 }}>
            {renderDriverFields(driver, handleSavedChange, index, null, true)}

            {ALLOW_DRIVER_RESET ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'stretch',
                }}
              >
                <Button
                  color="inherit"
                  onClick={() => resetSavedDriver(index)}
                  disabled={resettingDriverIndices.has(index)}
                  sx={{
                    backgroundColor: '#E2E5EA',
                    px: 4,
                    py: 1.5,
                    borderRadius: '20px',
                    fontWeight: 600,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: isMobile ? '0.8125rem' : '1.25rem',
                    color: '#1C2F3A',
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { sm: '120px' },
                  }}
                >
                  {resettingDriverIndices.has(index) ? (
                    <LoadingDots />
                  ) : (
                    'Reset'
                  )}
                </Button>
              </Box>
            ) : null}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                onClick={() => deleteSavedDriver(index)}
                disabled={deletingDriverIndices.has(index)}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.625rem',
                  fontWeight: 500,
                  color: '#FF3434',
                  borderBottom: deletingDriverIndices.has(index)
                    ? 'none'
                    : '1px solid #FF3434',
                  borderRadius: 0,
                  padding: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#FF3434',
                    borderBottom: deletingDriverIndices.has(index)
                      ? 'none'
                      : '2px solid #FF3434',
                  },
                }}
              >
                {deletingDriverIndices.has(index) ? (
                  <LoadingDots />
                ) : (
                  'Delete this Driver'
                )}
              </Button>
            </Box>
          </Box>
        </CollapsibleBoxComponent>
      ))}

      {/* Add New Driver Button */}
      {!showNewDriver && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
          }}
        >
          <Button
            onClick={() => setShowNewDriver(true)}
            sx={{
              height: '40px',
              backgroundColor: '#FF0074',
              borderRadius: '16px',
              px: 2,
              py: 1,
              gap: '12px',
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: '1rem',
              lineHeight: '1rem',
              letterSpacing: '-0.002em',
              fontFamily: 'BInter, Inter, sans-serif',
              textTransform: 'none',
              boxShadow: 'none',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                backgroundColor: '#e10068',
              },
            }}
          >
            Add Driver +
          </Button>
        </Box>
      )}

      {/* New Driver Form */}
      {showNewDriver && (
        <CollapsibleBoxComponent
          title="New driver"
          isMobile={isMobile}
          defaultExpanded={true}
        >
          <Box sx={{ px: isMobile ? 1.5 : 2 }}>
            {renderDriverFields(
              newDriver,
              handleNewChange,
              null,
              registerValidation
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                mt: 3,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
              }}
            >
              <Button
                color="inherit"
                onClick={() => {
                  setNewDriver(getFreshDriver());
                  setShowNewDriver(false);
                }}
                sx={{
                  backgroundColor: '#E2E5EA',
                  px: 4,
                  py: 1.5,
                  borderRadius: '20px',
                  fontWeight: 600,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: isMobile ? '0.8125rem' : '1.25rem',
                  color: '#1C2F3A',
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={addNewDriverToSaved}
                disabled={isSavingNewDriver}
                sx={{
                  backgroundColor: '#C7CA00',
                  px: 4,
                  py: 1.5,
                  borderRadius: '20px',
                  fontWeight: 600,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: isMobile ? '0.8125rem' : '1.25rem',
                  color: '#1C2F3A',
                  textTransform: 'none',
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: '205px' },
                }}
              >
                {isSavingNewDriver ? <LoadingDots /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </CollapsibleBoxComponent>
      )}
    </Box>
  );
};

export default DriverDetailsContent;
